import type { ReactNode } from "react";
import { site } from "./site";

// Inline formatting for CMS copy — bold, italic and links INSIDE a paragraph.
//
// Editors kept needing a word emphasised or a phrase linked in the middle of a
// sentence, and the fields are plain text: anything typed was rendered
// literally, so "<b>GCC</b>" appeared on the page with its tags showing.
//
// This turns a small, familiar syntax into real React elements. It never
// injects HTML — no dangerouslySetInnerHTML anywhere in this path — so a value
// pasted into the admin cannot introduce script, styles or markup beyond the
// three things below. Anything unrecognised stays literal text, escaped by
// React as before.
//
//   **bold**            <b>bold</b>       <strong>bold</strong>
//   *italic*            <i>italic</i>     <em>italic</em>
//   [label](https://…)  <a href="https://…">label</a>
//
// A link can carry a colour from the palette: [label](/page){teal}. Named,
// not hex, so the copy cannot drift off-brand — teal/accent, dark/ink, black,
// muted/grey, white.
//
// Both spellings are accepted because editors reach for either: the markdown
// shorthand is quicker to type, and the tag form is what gets pasted out of a
// Word document or another CMS.
//
// The pieces nest, so a phrase can be a link AND bold at once:
//
//   [**managed office space solutions**](/solutions/custom-built)
//
// Each match re-runs the parser over its own inner text, which always shrinks,
// so the recursion terminates on its own.

/** Links are allowed to these targets only. Everything else renders as text,
 *  which rules out javascript:, data: and any other scheme-based injection. */
function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;
  // Site-relative, anchor, and query-only links.
  if (/^[/#?]/.test(href)) return href;
  if (/^(https?:\/\/|mailto:|tel:)/i.test(href)) return href;
  return null;
}

/** Off-site, and therefore worth opening in a new tab.
 *
 *  An absolute link to tablespace.com is NOT: editors paste full URLs out of
 *  the address bar, and treating those as external sent readers to a new tab
 *  for a page on the same site. */
const isExternal = (href: string) => {
  if (!/^https?:\/\//i.test(href)) return false;
  try {
    return new URL(href).host !== new URL(site.url).host;
  } catch {
    return true;
  }
};

/** Colours an editor can put on a link, as {name} after it. Names rather than
 *  hex so the copy cannot drift off the palette. */
const LINK_COLOURS: Record<string, string> = {
  teal: "text-brand-accent-dark",
  accent: "text-brand-accent-dark",
  green: "text-brand-accent-dark",
  dark: "text-brand-ink",
  ink: "text-brand-ink",
  black: "text-black",
  muted: "text-brand-muted",
  grey: "text-brand-muted",
  gray: "text-brand-muted",
  white: "text-white",
};

// One pass, alternation ordered so the longer markers win: ** before *, and
// the tag forms before the shorthand. Capture groups per alternative.
const PATTERN = [
    // [label](href) with an optional {colour}
    String.raw`\[(?<mdLabel>[^\]]+)\]\((?<mdHref>[^)\s]+)\)(?:\{(?<mdColour>[A-Za-z-]+)\})?`,
    // <a href="…">label</a>  (single or double quotes, extra attributes ignored)
    String.raw`<a\s[^>]*href\s*=\s*["'](?<tagHref>[^"']+)["'][^>]*>(?<tagLabel>[\s\S]*?)<\/a>`,
    // <strong>…</strong> | <b>…</b>
    String.raw`<(?<strongTag>strong|b)\s*>(?<strongInner>[\s\S]*?)<\/\k<strongTag>>`,
    // <em>…</em> | <i>…</i>
    String.raw`<(?<emTag>em|i)\s*>(?<emInner>[\s\S]*?)<\/\k<emTag>>`,
    // **bold**
    String.raw`\*\*(?<starBold>[^*]+)\*\*`,
    // *italic* — not part of a ** run
    String.raw`(?<!\*)\*(?<starItalic>[^*\n]+)\*(?!\*)`,
].join("|");

// A FRESH regex per scan, never a shared one. The /g flag carries lastIndex
// as mutable state on the object, and renderInline recurses from inside its
// own exec loop — a shared instance let the inner scan reset lastIndex under
// the outer one, which restarted it from the beginning and never terminated.
// It cost a dev server to an out-of-memory kill before it was caught.
const scanner = () => new RegExp(PATTERN, "g");

/** True when a string holds anything this module would transform. Lets callers
 *  skip the work — and the extra wrapper element — for ordinary copy. */
export function hasInlineRich(s: string): boolean {
  return scanner().test(s);
}

/**
 * Render a CMS string with inline bold / italic / links.
 *
 * Returns the plain string untouched when there is nothing to format, so the
 * overwhelmingly common case adds no elements to the tree.
 */
export function renderInline(text: string): ReactNode {
  if (!text || !hasInlineRich(text)) return text;

  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  const re = scanner();

  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const whole = m[0];
    // Named groups: the numbered ones had to be re-counted by hand every time
    // an alternative was added, and the \5 / \7 backreferences silently
    // stopped matching the first time that was got wrong.
    const g = m.groups ?? {};

    if (g.mdLabel !== undefined || g.tagLabel !== undefined) {
      const label = (g.mdLabel ?? g.tagLabel) as string;
      const href = safeHref((g.mdHref ?? g.tagHref) as string);
      // An unusable href (javascript:, data:, anything not allowlisted) falls
      // back to the source text exactly as typed. Pushing just the label would
      // quietly rewrite the copy and could strand the tail of the rejected
      // URL — "[x](javascript:alert(1))" left a bare ")" on the page.
      if (!href) {
        out.push(whole);
      } else {
        const named = g.mdColour?.toLowerCase();
        const colour = named ? LINK_COLOURS[named] : undefined;
        out.push(
          <a
            key={key++}
            href={href}
            {...(isExternal(href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className={`underline decoration-brand-line underline-offset-4 transition-colors hover:text-brand-accent-dark${
              colour ? ` ${colour}` : ""
            }`}
          >
            {renderInline(label)}
          </a>,
        );
        // A colour nobody recognises is left on the page as the words the
        // editor typed, rather than swallowed — otherwise a typo in the name
        // silently deletes text from the paragraph.
        if (named && !colour) out.push(`{${g.mdColour}}`);
      }
    } else if (g.strongInner !== undefined || g.starBold !== undefined) {
      out.push(
        <strong key={key++} className="font-semibold">
          {renderInline((g.strongInner ?? g.starBold) as string)}
        </strong>,
      );
    } else if (g.emInner !== undefined || g.starItalic !== undefined) {
      out.push(<em key={key++}>{renderInline((g.emInner ?? g.starItalic) as string)}</em>);
    } else {
      out.push(whole);
    }
    last = m.index + whole.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}
